"use client";

import React, { useEffect, useState, useCallback } from "react";
import Image from "next/image";
import api from "@/lib/axiosInstance";
import { toast } from "sonner";
import { ProductProp } from "@/lib/types/productTypes";
import FilterPanel from "@/components/marketplace/FilterPanel";
import ProductGrid from "@/components/marketplace/ProductGrid";
import Pagination from "@/components/marketplace/Pagination";
import SearchIcon from "@/assets/icons/searchicon.svg";
import ProductSkeleton from "@/components/marketplace/ProductSkeleton";
import { PackageIcon } from "@/lib/icons/globalIcons";

export default function MarketplaceComponent() {
  const [activeTab, setActiveTab] = useState("all");
  const [products, setProducts] = useState<ProductProp[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    providers: [] as string[],
    popular: [] as string[],
    reviews: [] as string[],
  });
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const productsPerPage = 9;

  // Category mapping
  const categoryMap: Record<string, string> = {
    "solar panels": "SOLAR_PANEL",
    batteries: "BATTERY",
    inverters: "INVERTER",
    "charge controllers": "CHARGE_CONTROLLER",
    "solar kits": "SOLAR_KIT",
    other: "OTHER",
  };

  // Fetch products from backend - SINGLE SOURCE OF TRUTH
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const mappedCategory = categoryMap[activeTab];
        const params: Record<string, any> = {
          status: "ACTIVE",
          take: 100,
          skip: 0,
        };

        if (activeTab !== "all" && mappedCategory) {
          params.category = mappedCategory;
        }

        const res = await api.get("/products", { params });

        setProducts(res.data?.products || []);
        setTotalCount(
          res.data?.pagination?.total || res.data?.products?.length || 0
        );
      } catch (err) {
        console.error("Fetch error:", err);
        toast.error("Couldn't fetch products. Please refresh the page.");
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [activeTab]);

  // Apply client-side filters (includes instant search)
  const filteredProducts = React.useMemo(() => {
    let filtered = [...products];

    // 0. Search filter (instant, client-side)
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((p) =>
        p.name?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q) ||
        p.provider?.name?.toLowerCase().includes(q)
      );
    }

    // 1. Provider role filter
    if (appliedFilters.providers.length > 0) {
      filtered = filtered.filter((product) => {
        const providerRole = product.provider?.role;
        return appliedFilters.providers.some((filterProvider) => {
          if (filterProvider === "Manufacturers")
            return providerRole === "MANUFACTURER";
          if (filterProvider === "Distributors")
            return providerRole === "DISTRIBUTOR";
          if (filterProvider === "Admin")
            return providerRole === "ADMIN";
          return false;
        });
      });
    }

    // 2. Rating filter
    if (appliedFilters.reviews.length > 0) {
      filtered = filtered.filter((product) => {
        const rating = Number(product.rating) || 0;
        return appliedFilters.reviews.some((review) => {
          if (review === "4+ stars") return rating >= 4;
          if (review === "3+ stars") return rating >= 3;
          if (review === "2+ stars") return rating >= 2;
          return false;
        });
      });
    }

    // 3. Sorting
    if (appliedFilters.popular.length > 0) {
      const sortOption = appliedFilters.popular[0];

      if (sortOption === "Most Popular") {
        filtered.sort((a, b) => (b.reviewCount || 0) + Number(b.rating || 0) - ((a.reviewCount || 0) + Number(a.rating || 0)));
      } else if (sortOption === "Most Quoted") {
        filtered.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
      } else if (sortOption === "Most Sold") {
        filtered.sort((a, b) => (b.stockLevel !== undefined ? (10 - b.stockLevel) : 0) - (a.stockLevel !== undefined ? (10 - a.stockLevel) : 0));
      } else if (sortOption === "Newest") {
        filtered.sort(
          (a, b) =>
            new Date(b.createdAt || 0).getTime() -
            new Date(a.createdAt || 0).getTime()
        );
      }
    }

    return filtered;
  }, [products, appliedFilters, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const currentProducts = React.useMemo(() => {
    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    return filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  }, [filteredProducts, currentPage, productsPerPage]);

  // Reset to page 1 when filters/search/tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters, activeTab, searchQuery]);

  const handleFilterChange = useCallback(
    (filters: {
      providers: string[];
      popular: string[];
      reviews: string[];
    }) => {
      setAppliedFilters(filters);
    },
    []
  );

  const resetFilters = useCallback(() => {
    setAppliedFilters({ providers: [], popular: [], reviews: [] });
    setSearchQuery("");
    setActiveTab("all");
    setCurrentPage(1);
  }, []);

  const loadMore = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  const tabs = [
    { id: "all", label: "All Items", image: null },
    { id: "solar panels", label: "Solar Panels", image: "/assets/img/solar-panels.png" },
    { id: "batteries", label: "Batteries", image: "/assets/img/batteries.png" },
    { id: "inverters", label: "Inverters", image: "/assets/img/inverters.png" },
    { id: "charge controllers", label: "Charge Controllers", image: null },
    { id: "solar kits", label: "Solar Kits", image: "/assets/img/solar-bundles.png" },
    { id: "other", label: "Other", image: null },
  ];

  if (loading) return <ProductSkeleton />;

  return (
    <div className="min-h-screen bg-[#f4f6f6]">
      {/* Hero Header */}
      <div className="bg-[#002301] pt-28 pb-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="mp-dots" width="30" height="30" patternUnits="userSpaceOnUse">
                <circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#mp-dots)" />
          </svg>
        </div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-[#17BC2E]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <p className="text-[#17BC2E] text-sm font-semibold uppercase tracking-widest mb-2">Solar Marketplace</p>
          <h1 className="text-white text-3xl sm:text-4xl font-bold mb-2">
            Browse Verified Solar Products
          </h1>
          <p className="text-white/60 text-sm max-w-lg mb-6">
            Solar panels, batteries, inverters, kits, and more, sourced from trusted Nigerian manufacturers and distributors.
          </p>
          {/* Search bar in hero */}
          <div className="relative max-w-xl">
            <input
              type="text"
              placeholder="Search for solar panels, batteries, inverters..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-5 pr-12 py-3.5 text-sm bg-white rounded-xl border-0 shadow-lg focus:ring-2 focus:ring-[#17BC2E] transition-all outline-none"
            />
            <SearchIcon className="absolute right-4 top-3.5 h-5 w-5 text-gray-400" />
          </div>
        </div>
      </div>

      <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Tabs row */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 relative cursor-pointer hover:scale-105 ${
                activeTab === tab.id
                  ? "text-[#17BC2E] bg-[#17BC2E]/10 ring-1 ring-[#17BC2E]/30"
                  : "text-gray-700 hover:text-[#17BC2E] hover:bg-gray-100 bg-white"
              }`}
            >
              {tab.image && (
                <Image
                  src={tab.image}
                  alt={tab.label}
                  width={18}
                  height={18}
                  className="object-contain rounded-sm"
                />
              )}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Filters */}
          <div className="lg:w-1/4">
            <FilterPanel
              onFilterChange={handleFilterChange}
              onReset={resetFilters}
              activeFilters={appliedFilters}
            />
          </div>

          {/* Product Grid */}
          <div className="lg:w-3/4">
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Showing {currentProducts.length} of {filteredProducts.length}{" "}
                products
              </p>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg">
                <PackageIcon className="w-32 h-32 text-gray-500" />

                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No products found
                </h3>
                <p className="text-gray-500 text-sm mb-4">
                  Try adjusting your filters or search terms
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="px-4 py-2 bg-[#17BC2E] text-white rounded-full hover:bg-[#15a327] transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <>
                <ProductGrid products={currentProducts} />
                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    onLoadMore={loadMore}
                    showLoadMore={currentPage < totalPages}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}
